/**
 * Kolumny „Proces domyślny" i „Układ formularza" w tabelach słowników typów rekordu (typy
 * dokumentów, typy zadań). Wspólny szkielet: wartość filtra z `NONE_FILTER_VALUE` dla pustego
 * wskazania, opcje filtra, komórka edytowalna (`DefaultProcessCell` / `LayoutPickerCell`) albo
 * sama nazwa. Różnice (kto może edytować, zapis, `data-testid`, obsługa typów globalnych) wołający
 * podaje funkcjami od wiersza.
 */

import type { TFunction } from 'i18next';

import type { ColumnDefinition } from '@/components/configurable-table/types';
import { DefaultProcessCell } from '@/components/kartoteki/DefaultProcessCell';
import { LayoutPickerCell } from '@/components/kartoteki/LayoutPickerCell';
import {
  defaultProcessFilterOptions,
  type DefaultProcessOption,
} from '@/lib/record-types/default-process';
import { type FormLayoutOption, NONE_FILTER_VALUE } from '@/lib/record-types/form-layout';

export interface DefaultProcessColumnOptions<T> {
  t: TFunction;
  header: string;
  processes: DefaultProcessOption[];
  /** Wskazany proces wiersza; `null` = brak. */
  getProcessId: (row: T) => string | null;
  /** `true` = lista wyboru w miejscu; `false` = sama nazwa procesu. */
  canEdit: (row: T) => boolean;
  /** Zapis wskazania tego wiersza trwa. */
  isPending: (row: T) => boolean;
  onChange: (row: T, processId: string | null) => void;
  testId: (row: T) => string;
}

export function defaultProcessColumn<T>({
  t,
  header,
  processes,
  getProcessId,
  canEdit,
  isPending,
  onChange,
  testId,
}: DefaultProcessColumnOptions<T>): ColumnDefinition<T> {
  return {
    id: 'defaultProcess',
    accessor: (row) => getProcessId(row) ?? NONE_FILTER_VALUE,
    header,
    filterable: true,
    headerFilter: {
      category: 'select',
      options: defaultProcessFilterOptions(processes, t),
    },
    renderCell: (_v, row) => (
      <DefaultProcessCell
        processes={processes}
        value={getProcessId(row)}
        canEdit={canEdit(row)}
        pending={isPending(row)}
        onChange={(nextId) => onChange(row, nextId)}
        data-testid={testId(row)}
      />
    ),
  };
}

export interface FormLayoutColumnOptions<T> {
  header: string;
  layouts: FormLayoutOption[];
  /** Etykieta opcji „— brak —" w filtrze kolumny. */
  noneLabel: string;
  /** Przypisany układ wiersza; `null` = kaskada domyślna kartoteki. */
  getLayoutId: (row: T) => string | null;
  /** `true` = wybór układu w miejscu; `false` = sama nazwa układu. */
  canEdit: (row: T) => boolean;
  /** Zapis układu; Promise odrzucony zostawia okno wyboru otwarte z komunikatem. */
  onCommit: (row: T, layoutId: string | null) => void | Promise<void>;
  testId: (row: T) => string;
}

export function formLayoutColumn<T>({
  header,
  layouts,
  noneLabel,
  getLayoutId,
  canEdit,
  onCommit,
  testId,
}: FormLayoutColumnOptions<T>): ColumnDefinition<T> {
  return {
    id: 'formLayout',
    accessor: (row) => getLayoutId(row) ?? NONE_FILTER_VALUE,
    header,
    filterable: true,
    headerFilter: {
      category: 'select',
      options: [
        { value: NONE_FILTER_VALUE, label: noneLabel },
        ...layouts.map((l) => ({ value: l.id, label: l.name })),
      ],
    },
    renderCell: (_v, row) =>
      canEdit(row) ? (
        <LayoutPickerCell
          layouts={layouts}
          value={getLayoutId(row)}
          onCommit={(nextId) => onCommit(row, nextId)}
          data-testid={testId(row)}
        />
      ) : (
        (layouts.find((l) => l.id === getLayoutId(row))?.name ?? (
          <span className="text-text-muted">—</span>
        ))
      ),
  };
}
